import React, { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import api from "../../services/axiosInstance";
import ReportPreviewModal from "../common/ReportPreviewModal";
import { buildIssueNotePDF, downloadIssueNotePDF } from "../../utils/issueNotePdfUtils";

/**
 * Material Issue Note preview / print modal.
 * Fetches issue note details, generates a branded PDF via jsPDF (matching the
 * GRN/PR report style), then shows it in the shared ReportPreviewModal with
 * preview + download actions.
 *
 * Usage:
 *   <IssueNotePreviewModal iinId={id} visible={visible} onCancel={...} />
 */
const IssueNotePreviewModal = ({ iinId, visible, onCancel }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfDataUri, setPdfDataUri] = useState(null);

  const fetchIssueNoteData = useCallback(async () => {
    setLoading(true);
    setPdfDataUri(null);
    try {
      const res = await api.get(`/inventory/issuing/list.php?iin_id=${iinId}`);
      if (res.data && res.data.status === "success" && res.data.dataset) {
        setData(res.data.dataset);
        try {
          const doc = await buildIssueNotePDF(res.data.dataset);
          setPdfDataUri(doc.output("bloburl"));
        } catch (pdfError) {
          console.error("PDF generation failed:", pdfError);
          message.error("Failed to generate PDF preview.");
        }
      } else {
        message.error("Failed to load Material Issue Note details.");
      }
    } catch (error) {
      message.error("Error loading Material Issue Note.");
    } finally {
      setLoading(false);
    }
  }, [iinId]);

  useEffect(() => {
    if (visible && iinId) {
      fetchIssueNoteData();
    } else if (!visible) {
      setData(null);
      setPdfDataUri(null);
    }
  }, [visible, iinId, fetchIssueNoteData]);

  const handleDownload = async () => {
    try {
      if (!data) return;
      await downloadIssueNotePDF(data);
      message.success("PDF downloaded successfully.");
    } catch (error) {
      console.error("PDF download failed:", error);
      message.error("Failed to download PDF.");
    }
  };

  return (
    <ReportPreviewModal
      visible={visible}
      onCancel={onCancel}
      title={`Material Issue Note - ${data?.iin_ref || ""}`}
      pdfDataUri={pdfDataUri}
      loading={loading}
      showFiltersOption={false}
      onDownload={handleDownload}
    />
  );
};

export default IssueNotePreviewModal;
